import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const { formatApiError } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); setShowCurrent(false); setShowNext(false); };

  // Simple strength heuristic — good enough to guide the user, not a security control
  const strength = (() => {
    if (!next) return { score: 0, label: "" };
    let s = 0;
    if (next.length >= 8) s++;
    if (next.length >= 12) s++;
    if (/[A-Z]/.test(next) && /[a-z]/.test(next)) s++;
    if (/\d/.test(next)) s++;
    if (/[^A-Za-z0-9]/.test(next)) s++;
    return { score: s, label: ["Too short","Weak","Fair","Good","Strong","Very strong"][s] };
  })();
  const strengthColor = ["bg-zinc-200","bg-red-500","bg-orange-500","bg-amber-500","bg-emerald-500","bg-emerald-600"][strength.score];

  const mismatch = confirm.length > 0 && next !== confirm;
  const canSubmit = current && next.length >= 8 && next === confirm && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-[#0055FF]" />
            Change password
          </DialogTitle>
          <DialogDescription>
            Rotate your account password. You&apos;ll stay signed in on this device.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Current password</Label>
            <div className="relative">
              <Input
                type={showCurrent ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="change-pw-current-input"
                className="h-11 pr-10 rounded-md font-mono text-xs"
              />
              <button type="button" onClick={() => setShowCurrent((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                aria-label={showCurrent ? "Hide" : "Show"}>
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">New password</Label>
            <div className="relative">
              <Input
                type={showNext ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="change-pw-new-input"
                className="h-11 pr-10 rounded-md font-mono text-xs"
              />
              <button type="button" onClick={() => setShowNext((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                aria-label={showNext ? "Hide" : "Show"}>
                {showNext ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {next && (
              <div className="pt-1" data-testid="change-pw-strength">
                <div className="flex gap-1 h-1">
                  {[1,2,3,4,5].map((i) => (
                    <div key={i} className={`flex-1 rounded-full transition-colors ${i <= strength.score ? strengthColor : "bg-zinc-200 dark:bg-zinc-800"}`} />
                  ))}
                </div>
                <div className="text-[11px] mt-1.5 text-zinc-500 dark:text-zinc-400 flex justify-between">
                  <span>{strength.label}</span>
                  <span className="font-mono">{next.length} chars · min 8</span>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Confirm new password</Label>
            <Input
              type={showNext ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
              data-testid="change-pw-confirm-input"
              className={`h-11 rounded-md font-mono text-xs ${mismatch ? "border-red-500 focus-visible:ring-red-500/30" : ""}`}
            />
            {mismatch && <p className="text-xs text-red-600 dark:text-red-400">Passwords don&apos;t match yet.</p>}
            {!mismatch && confirm && next === confirm && (
              <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Match
              </p>
            )}
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}
              data-testid="change-pw-cancel-btn">
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}
              data-testid="change-pw-submit-btn"
              className="bg-[#0055FF] hover:bg-[#0040CC] text-white">
              {busy ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Updating…</>) : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
